import { Button, Spinner } from '@/ui-kit'
import { useUiKit } from '@/ui-kit/providers'
import { formatLastActivity } from '@/utils'
import { CircleAlert } from 'lucide-react'
import { ReactNode, useMemo, useState } from 'react'
import { WorkerBase } from '../../api/models'
import { isVersionOutdated, useCurrentConfiguration } from '../../api/version'
import { getDeviceDescription } from '../../utils/getDeviceDescription'
import { DeviceOutdatedVersionTooltip } from '../DeviceOutdatedVersionTooltip'
import { DevicesMobileList } from './DevicesMobileList'
import { DevicesTable } from './DevicesTable'
import dayjs from 'dayjs'

type ColumnDef<T> = {
  name: string
  value: (worker: T) => string | ReactNode
  cellClassName?: string
  excludeOnMobile?: boolean
  mobileRender?: (worker: T) => ReactNode
}

export type DevicesListProps<T> = {
  columns: ColumnDef<T>[]
  workers: T[]
  isFetching?: boolean
  selectedWorkerId?: string
  setSelectedWorkerId?: (index: string) => void
  meta?: {
    noHover?: boolean
    placeholderText?: string
  }
}
export const DevicesList = <T extends WorkerBase>({
  columns,
  workers,
  isFetching,
  selectedWorkerId,
  setSelectedWorkerId,
  meta
}: DevicesListProps<T>) => {
  const { data: systemConfig } = useCurrentConfiguration()
  const { isMobile } = useUiKit()
  const [isShowingAll, setIsShowingAll] = useState(false)
  const workersToShow = workers.slice(0, isShowingAll ? workers.length : 3)

  const DEFAULT_DEVICES_COLUMN_DEFS = useMemo(
    () => [
      {
        name: 'Type',
        value: (worker: WorkerBase) => (
          <div className="relative flex items-center gap-2">
            {!worker.activitySlotExpiresAt &&
              worker.metadata?.lastTimeActive &&
              dayjs(worker.metadata.lastTimeActive).isAfter(dayjs().subtract(90, 'minutes')) && (
                <CircleAlert height={20} width={20} className="h-4 w-4 text-orange" />
              )}
            {worker.metadata &&
            systemConfig &&
            isVersionOutdated({
              version: worker.metadata?.clientVersion,
              lastTimeActive: worker.metadata.lastTimeActive,
              type: worker.type,
              config: systemConfig
            }) ? (
              <DeviceOutdatedVersionTooltip type={worker.type} />
            ) : worker.type.toLowerCase() === 'extension' ? (
              'Browser'
            ) : (
              'Mobile'
            )}
          </div>
        )
      },
      {
        name: 'Description',
        value: (worker: WorkerBase) => (worker.metadata ? getDeviceDescription(worker.metadata.userAgent) : '-'),
        excludeOnMobile: true
      },
      {
        name: 'IP Address',
        value: (worker: WorkerBase) => worker.metadata?.ip || '-'
      },
      {
        name: 'Last Active',
        value: (worker: WorkerBase) => formatLastActivity(worker.metadata?.lastTimeActive || '-')
      }
    ],
    [systemConfig]
  )
  const devicesColumns = [...DEFAULT_DEVICES_COLUMN_DEFS, ...columns]

  const componentProps = {
    columns: devicesColumns,
    workers: workersToShow,
    selectedWorkerId,
    setSelectedWorkerId,
    meta
  }

  return (
    <div className="flex flex-col">
      {isFetching ? (
        <div className="flex w-full justify-center">
          <Spinner className="mt-8" />
        </div>
      ) : (
        <>
          {isMobile ? <DevicesMobileList {...componentProps} /> : <DevicesTable {...componentProps} />}
          {!isShowingAll && workers?.length > 3 && (
            <Button
              className="max-mobile:mt-4 mb-3.5"
              onClick={() => setIsShowingAll(true)}
              variant="bordered-gray"
              size="small"
            >
              Show all
            </Button>
          )}
        </>
      )}
    </div>
  )
}
